import React, { useState, useEffect } from 'react';
import { Card, Col } from 'antd';
import { ChartDataItem, Metric } from '../../interfaces/IMetricChart';
import { processData } from './Trend Analysis/MetricsChart';
import { FallOutlined, RiseOutlined } from '@ant-design/icons';
import { PageType } from '../../utils/AppEnum';

interface MetricCardProps {
  name: string;
  target: string;
  units: string;
  value: string | number;
  metric: Metric[];
  is_positive: boolean;
  isTargetEnabled: boolean;
  setSelectedMetricCard: React.Dispatch<React.SetStateAction<string | undefined>>;
  metricType?: string;
  pageType?:string;
  weight?:string | null;
}

export const formatValue = (val: number) => {
  return Number.isInteger(val) ? val.toString() : val.toFixed(1).replace(/\.0$/, '');
};

const CustomArrowRight: React.FC<{
  style?: React.CSSProperties;
  className?: string;
}> = ({ style, className }) => {
  return (
    <span className={className} style={{ ...style, display: 'inline-flex', fontSize: '45px' }}>
      <svg
        viewBox="0 0 1024 1024"
        width="1em"
        height="1em"
        fill="currentColor"
      >
        <path
          d="M190 512h580"
          stroke="currentColor"
          strokeWidth="60"
          fill="none"
        />

        <path
          d="M770 512 L670 412 L670 612 Z"
          stroke="currentColor"
          strokeWidth="20"
          strokeLinejoin="round"
          fill="currentColor"
        />
      </svg>
    </span>
  );
};

const calculateTrendSlope = (data: { timestamp: number; value: number }[]): number => {
  const validData = data.filter(
    (point) =>
      typeof point.timestamp === "number" &&
      !isNaN(point.timestamp) &&
      typeof point.value === "number" &&
      !isNaN(point.value)
  );

  const N = validData.length;
  if (N < 2) return 0;

  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

  validData.forEach((point, index) => {
    const x = index;
    const y = point.value;

    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
  });

  const numerator = N * sumXY - sumX * sumY;
  const denominator = N * sumX2 - sumX * sumX;

  const slope = denominator !== 0 ? numerator / denominator : 0;

  return isNaN(slope) ? 0 : slope;
};

const TrendArrow: React.FC<{ slope: number; isPositive?: boolean; value?: number | string; target?: string }> = ({
  slope,
  isPositive,
  value,
  target
}) => {
  let color = "black";

  if (target && value !== undefined) {
    if (slope === 0) {
      color = "black";
    } else {
      const hasImproved = slope > 0;

      if (isPositive === true) {
        color = hasImproved ? "green" : "red";
      } else if (isPositive === false) {
        color = hasImproved ? "red" : "green";
      } else {
        color = "black";
      }
    }
  }

  return (
    <span style={{ fontSize: "22px", marginLeft: "4px", color }}>
      {slope > 0 ? <RiseOutlined /> : slope < 0 ? <FallOutlined /> : <CustomArrowRight />
      }
    </span>
  );
};

const MetricCard: React.FC<MetricCardProps> = ({
  name,
  target,
  units,
  value,
  metric,
  is_positive,
  isTargetEnabled,
  setSelectedMetricCard,
  metricType = "performance_metric",
  pageType,
  weight
}) => {
  const [dataPoints, setDataPoints] = useState<ChartDataItem[]>([]);

  const borderColor = metricType === "health_metric" ? "#10b981" : "#2563eb";

  const baseStyle = {
    marginBottom: '8px',
    marginLeft: '4px',
    marginRight: '4px',
    border: `1px solid ${borderColor}`,
    boxShadow: `0 1px 2px ${borderColor}20`,
    width: '160px',
    height: '100px',
  };

  const style = {
    ...baseStyle,
    ...(isTargetEnabled && { height: '120px' }),
  }

  const styleData = {
    fontSize: '16px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };

  const targetDifference = parseFloat(value as string) - parseFloat(target);
  const roundedTargetDifference = formatValue(targetDifference);

  let targetStyle = {
    color: 'green',
    backgroundColor: 'transparent',
  };
  let targetText = '';
  let vsTargetText = 'vs. Target';

  if (targetDifference > 0) {
    targetStyle = {
      color: is_positive ? "green" : "red",
      backgroundColor: is_positive ? '#dff0d8' : '#ffcccc',
    };
    targetText = `+${roundedTargetDifference}`;
  } else if (targetDifference < 0) {
    targetStyle = {
      color: is_positive ? "red" : "green",
      backgroundColor: is_positive ? '#ffcccc' : '#dff0d8',
    };
    targetText = `${roundedTargetDifference}`;
  } else if (targetDifference === 0) {
    targetText = 'On target';
    vsTargetText = '';
  }

  useEffect(() => {
    if (metric && typeof weight === "string") {
      setDataPoints(processData(metric, weight??null));
    } else {
      setDataPoints([]);
    }
  }, [metric,weight]);

  const trendSlope = calculateTrendSlope(
    dataPoints
      .filter((d) => d.value !== undefined)
      .map((d) => ({
        timestamp: new Date(d.date).getTime(),
        value: d.value as number,
      }))
  );

  const handleCardClick = () => {
    setSelectedMetricCard(name);
  };

  const cardClass = `
  ${metricType === "health_metric" ? "metric-card-health" : "metric-card-performance"} 
  ${pageType !== PageType.WIP_DASHBOARD ? "cursor-pointer" : ""}
`;


  return (
    <Col className="card-width">
      <Card
        style={style}
        onClick={handleCardClick}
        className={`hover:shadow-md transition-shadow ${cardClass}`}
        bodyStyle={{ padding: '14px' }}
      >
        <div className="absolute top-[0px] right-2">
          <TrendArrow slope={trendSlope} isPositive={is_positive} value={value} target={target} />
        </div>
        <div className="mt-4 ml-[-4px]">
          <span>{name}</span>
          <h1 style={styleData}>
            {value} {units}
          </h1>
          {isTargetEnabled && (
            <div style={{ fontSize: "14px", display: "flex", alignItems: "center" }}>
              {target ? (
                <>
                  <span style={targetStyle} className="target-text">{targetText}</span>
                  {vsTargetText && <span style={{ marginLeft: "5px" }}>{vsTargetText}</span>}
                </>
              ) : (
                <span className='mb-[6px] mt-[6px]'>No Target</span>
              )}
            </div>
          )}
        </div>
      </Card>
    </Col>
  );
};

export default MetricCard;